import { Component, OnInit, Input, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Output } from '@angular/core';

import { FormRendererComponent } from '../../../shared/components/form-renderer/form-renderer.component';
import { FormInstanceService } from '../../services/form-instance.service';
import { FormInstance } from '../../models/site-content/form-instance.model';
import { Form } from '../../models/form-builder/form.model';
import { Logging } from '../../logging';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { ScriptableBaseComponent } from '../scriptable-base/scriptable-base.component';
import { TreeModule } from 'primeng/tree';
import { Router } from '@angular/router';
import { FlexibleSelectionFieldService } from '../../services/flexible-selection-field.service';
import { WorkflowTransition } from '../../models/workflow-transition.model';
import { CurrentSiteService } from '../../services/current-site.service';

@Component({
    selector: 'app-form-filler',
    templateUrl: './form-filler.component.html',
    styleUrls: ['./form-filler.component.scss'],
    standalone: false
})
export class FormFillerComponent extends ScriptableBaseComponent implements OnInit {
    // Define properties.
    // Input properties.
    @Input() formInstance: FormInstance;
    @Input() isClosable: boolean;

    @Output() save: EventEmitter<FormInstance> = new EventEmitter<FormInstance>();
    @Output() reset: EventEmitter<FormInstance> = new EventEmitter<FormInstance>();
    @Output() close: EventEmitter<FormInstance> = new EventEmitter<FormInstance>();

    //VNEXT-310: KLW - Create a new emitter to update the form instance for the Structure component after a save or workflow transition
    @Output() updateFormInstance: EventEmitter<FormInstance> = new EventEmitter<FormInstance>();

    @ViewChild('originalFormRenderer') formRenderer: FormRendererComponent;

    // Constructor.
    public constructor(private formInstanceService: FormInstanceService,
        private flexibleFieldService: FlexibleSelectionFieldService,
        private currentSiteService: CurrentSiteService,
        private router: Router,
        public dialog: MatDialog,
        public containingElement: ElementRef) {
        super(containingElement);
    }

    // Life cycle methods.
    public ngOnInit(): void {
    }

    // Accessor methods.
    public get Form(): Form {
        return (this.formInstance.form)
    }

    public get FormInstance(): FormInstance {
        return this.formInstance;
    }

    public get FormRenderer(): FormRendererComponent {
        return this.formRenderer;
    }

    public get IsClosable(): boolean {
        return this.isClosable;
    }

    //VNEXT-950: KLW - Logic to show and hide the form and transition buttons
    private hideShowSaveDiscard: boolean = true;
    public get HideShowSaveDiscard(): boolean {
        return this.hideShowSaveDiscard;
    }

    private hideShowTransitions: boolean = true;
    public get HideShowTransitions(): boolean {
        return this.hideShowTransitions;
    } 

    private conflictingFormInstance: FormInstance;

    public workflowChangeConflict: boolean = false;

    public get ConflictingFormInstance(): FormInstance {
        ////VNEXT-950: KLW - Logic to show and hide the form and transition buttons
        if (this.conflictingFormInstance && this.conflictingFormInstance.canView) {
            this.hideShowSaveDiscard = false;
            this.hideShowTransitions = false;
        }
        else{
            this.hideShowSaveDiscard = true;
            this.hideShowTransitions = true;
        }

        return this.conflictingFormInstance;
    }

    public get ConflictingFormInstanceFriendlyModifiedDate(): string {
        let d = new Date(this.conflictingFormInstance.modifiedDate);
        return " at " + d.toLocaleTimeString() + " on " + d.toLocaleDateString();
    }

    public saveRenderer() {
        this.formRenderer.saveClicked();
    }

    // Form event methods.
    public onFormInstanceSave(event: any): Promise<FormInstance> {
        if (event['form'] != null) {
            let form: Form = event['form'];
            form.removeAnyCircularDependencies(); // Removes any circular depenencies related to grid cascading drop-down fields.
        }

        this.formInstance.formButtonsDisabled = false;

        let savePromise: Promise<FormInstance> = this.formInstanceService.updateContent(event.formInstance);
        savePromise.then(
            updatedFormInstance => {
                // Update my form instance, causing the child
                // form renderer components ngOnChanges() to fire.
                this.formInstance = updatedFormInstance;
                this.save.emit(this.formInstance);
                this.conflictingFormInstance = null;
                //Logging.log('FormFillerComponent: updated my form instance elements after saving it.');
            }
        ).catch(error => {
            console.log("Error while saving form");
            console.log(error);
            if ('error' in error) {
                this.containingElement.nativeElement.scrollTo(0, 0);
                this.conflictingFormInstance = FormInstance.loadFromDeserializedNetworkData(error.error);
            }
        }).finally(() => {
            if (this.conflictingFormInstance == null) {
                this.afterFormInstanceSave();
            }
        });

        return savePromise;
    }

    private afterFormInstanceSave() {
        this.formInstance.formButtonsDisabled = false;

        if (this.formRenderer != null) {
            this.formRenderer.savedCompleted();
            this.flexibleFieldService.requestFlexibleFieldInstructionsForFormInstance(this.formInstance.id);
        }


        let dirtyElements = this.formInstance.formInstanceElements.filter(x => { return x.UserUpdatedData });
        for (let el of dirtyElements) {
            el.UserUpdatedData = false;
        }
    }

    public navigateToSection(section: string) {
        var element = document.getElementById(section);
        element.scrollIntoView();
    }

    public onNotifySubscribers(event: any): void {
        this.formInstanceService.notifySubscribers(this.formInstance).then(x => {
        });
    }

    public onTransition(workflowTransition: WorkflowTransition): void {
        let dialogueTitle: string = (workflowTransition.changeWorkflowStateDialogTitle != null) && (workflowTransition.changeWorkflowStateDialogTitle.trim() != '') ?
            workflowTransition.changeWorkflowStateDialogTitle : `Change Workflow State`;
        let confirmationMessage: string = (workflowTransition.transitionConfirmationDialogMessage != null) && (workflowTransition.transitionConfirmationDialogMessage.trim() != '') ?
            workflowTransition.transitionConfirmationDialogMessage : `Are you sure you want to transition the workflow state from ${workflowTransition.name}?`;
           
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            maxWidth: "400px",
            maxHeight: "300px",
            data: new ConfirmationDialogModel(
                dialogueTitle, //`Change Workflow State`,
                confirmationMessage)// `Are you sure you want to ${event}?`)
        });

        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                let canSaveAsDraft = this.formInstance.currentWorkflowStateIsStartState;
                this.formRenderer.CheckFormValidations().then(() => {
                    this.formRenderer.buildFormInstance(canSaveAsDraft, "transition").then(x => {
                        // PHarvey - Because of new requirements for VNEXT-1184, if the form is found to be invalid upon transitioning
                        // the user is given the option of aborting the transition OR saving a draft in the current workflow state
                        if (x) {
                            this.formInstance = x;
                            if (this.formInstance.hasFailedClientValidation) {
                                // the user has chosen "Save Draft" so all we want to do is save the form without transitioning it
                                this.formRenderer.emitOnSave();
                            } else {
                                this.formInstanceService.transitionWorkflowState(this.formInstance).then(formInstance => {

                                    this.formInstance.currentWorkflowState = formInstance.currentWorkflowState;
                                    this.formInstance.currentWorkflowStateId = formInstance.currentWorkflowStateId;
                                    this.formInstance.currentWorkflowStateOrder = formInstance.currentWorkflowStateOrder;

                                    this.formInstance.selectedWorkflowTransition = formInstance.selectedWorkflowTransition;
                                    this.formInstance.selectedWorkflowTransitionEndStateId = formInstance.selectedWorkflowTransitionEndStateId;
                                    this.formInstance.workflowStateTransitions = formInstance.workflowStateTransitions;
                                    this.formInstance.rowVersion = formInstance.rowVersion;

                                    this.formInstance.accessLostAfterTransition = formInstance.accessLostAfterTransition;

                                    // We need to update the 'canEdit' permission as this tells the form renderer
                                    // when it's safe to request conditional logic instructions from the server.
                                    this.formInstance.canEdit = formInstance.canEdit;

                                    this.afterFormInstanceSave();

                                    // pharvey
                                    // requested by Dan 1/19/2023 ahead of upcoming demo
                                    // added to ensure that any permissions changes caused by the transition take effect
                                    // (don't reload if the user lost access - we have a more pleasant way of informing them of that)
                                    if (workflowTransition.takeUserToSiteHomePageAfterTransitionApplied) {
                                        let homePageURL: string = `site/${this.currentSiteService.Site.id}/home-page`;
                                        this.router.navigateByUrl(homePageURL);
                                    } else if (!this.formInstance.accessLostAfterTransition) {
                                        this.router.navigateByUrl(this.router.url);
                                    }

                                }).catch(() => {
                                    this.workflowChangeConflict = true;
                                    this.formInstance.formButtonsDisabled = false;
                                });
                            }
                        }
                    });
                });
            }
            else {
                this.formInstance.formButtonsDisabled = false;
            }
        });
    }

    public onFormInstanceReset(event: any): void {
        this.formInstance = event.formInstance;
        this.conflictingFormInstance = null;
        this.reset.emit(this.formInstance);
        this.formRenderer.formInstanceReset();
    }

    public onFormInstanceClose(event: any): void {
        this.close.emit(event);
    }

    public onRequestService(hshEventData: any): void {
        if (hshEventData) {
            hshEventData['service'] = this.formInstanceService;
        }
    }

    // Note:  when the following method, hasUnsavedChanges(), returns true, it causes the browser to display 
    //        a dialogue asking the user if she/he wants to navigate away as there are unsaved changes.
    //
    // Note:  the following method only matters if the component implements interface HasUnsavedChanges.
    public hasUnsavedChanges(): boolean {
        let hasUpdates: boolean = false;
        for (let el of this.formInstance.formInstanceElements) {
            if (el.UserUpdatedData) {
                hasUpdates = true;
                break;
            }
        }
        return hasUpdates;
    }

    //VNEXT-951: KLW - Changes to bypass leave site pop-up warning
    public hasBypassSitePopUpWarning(): boolean {
        let hasBypassSite: boolean = false;
        for (let el of this.formInstance.formInstanceElements) {
            if (el.BypassSitePopUpWarning) {
                hasBypassSite = true;
                break;
            }
        }
        return hasBypassSite;
    }
}
